import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    Grid,
    Card,
    CardContent,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Paper,
    Avatar,
    Button,
    Divider,
    TextField,
    Alert,
    Stepper,
    Step,
    StepLabel,
    StepContent,
    Tab,
    Tabs,
    useTheme,
    useMediaQuery
} from '@mui/material';
import {
    Nature,
    CheckCircle,
    Cancel,
    LocalFlorist,
    Restaurant,
    Grass,
    ArrowBack,
    Apple,
    Yard,
    TrendingUp,
    Calculate,
    LocationOn,
    Phone,
    Schedule,
    EmojiEvents,
    AttachMoney,
    Timer,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

function TabPanel(props) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

const OrganicWastePage = () => {
    const [monthlyOrganic, setMonthlyOrganic] = useState(25);
    const [tabValue, setTabValue] = useState(0);
    const [activeStep, setActiveStep] = useState(0);
    const [expandedAccordion, setExpandedAccordion] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const organicItems = [
        {
            category: "Food Scraps",
            icon: <Apple sx={{ fontSize: 40, color: '#4caf50' }} />,
            items: ["Fruit peels", "Vegetable scraps", "Coffee grounds", "Tea bags", "Eggshells", "Bread crusts", "Rice", "Pasta"],
            color: "#4caf50",
            decompositionTime: "2-8 weeks",
            carbonRatio: "Green (High Nitrogen)",
            compostValue: "High nutrients"
        },
        {
            category: "Yard Waste",
            icon: <Yard sx={{ fontSize: 40, color: '#8bc34a' }} />,
            items: ["Grass clippings", "Leaves", "Small branches", "Plant trimmings", "Flowers", "Hedge clippings", "Tree bark"],
            color: "#8bc34a",
            decompositionTime: "3-12 weeks",
            carbonRatio: "Brown (High Carbon)",
            compostValue: "Excellent structure"
        },
        {
            category: "Kitchen Waste",
            icon: <Restaurant sx={{ fontSize: 40, color: '#689f38' }} />,
            items: ["Vegetable ends", "Spoiled fruits", "Herb stems", "Nutshells", "Corn cobs", "Potato peels", "Carrot tops"],
            color: "#689f38",
            decompositionTime: "1-6 weeks",
            carbonRatio: "Green (High Nitrogen)",
            compostValue: "Rich in minerals"
        },
        {
            category: "Biodegradable Items",
            icon: <LocalFlorist sx={{ fontSize: 40, color: '#558b2f' }} />,
            items: ["Paper towels", "Napkins", "Hair clippings", "Cotton balls", "Wooden items", "Natural fabrics", "Sawdust"],
            color: "#558b2f",
            decompositionTime: "4-16 weeks",
            carbonRatio: "Brown (High Carbon)",
            compostValue: "Good bulking agent"
        }
    ];

    const compostingMethods = [
        {
            method: "Traditional Pile Composting",
            description: "Simple outdoor heap method for large quantities",
            timeframe: "3-6 months",
            spaceRequired: "Large yard",
            maintenance: "Turn monthly",
            icon: "🗂️",
            difficulty: "Easy",
            cost: "৳500-2000"
        },
        {
            method: "Bin Composting",
            description: "Contained system perfect for urban gardens",
            timeframe: "2-4 months",
            spaceRequired: "Small yard",
            maintenance: "Turn bi-weekly",
            icon: "🗃️",
            difficulty: "Easy",
            cost: "৳1500-5000"
        },
        {
            method: "Tumbler Composting",
            description: "Rotating container for faster decomposition",
            timeframe: "6-8 weeks",
            spaceRequired: "Minimal",
            maintenance: "Turn daily",
            icon: "🥁",
            difficulty: "Medium",
            cost: "৳8000-15000"
        },
        {
            method: "Vermicomposting",
            description: "Worm-based composting for nutrient-rich output",
            timeframe: "2-3 months",
            spaceRequired: "Indoor/Outdoor",
            maintenance: "Weekly feeding",
            icon: "🪱",
            difficulty: "Medium",
            cost: "৳3000-8000"
        },
        {
            method: "Bokashi Composting",
            description: "Fermentation method using beneficial microorganisms",
            timeframe: "2-4 weeks",
            spaceRequired: "Kitchen counter",
            maintenance: "Daily layering",
            icon: "🧪",
            difficulty: "Advanced",
            cost: "৳2000-6000"
        }
    ];

    const compostingBenefits = [
        {
            title: "Soil Enrichment",
            description: "Adds essential nutrients and improves soil structure for better plant growth",
            impact: "30-50% better plant growth",
            icon: "🌱",
            timeToSee: "2-4 weeks"
        },
        {
            title: "Water Retention",
            description: "Helps soil retain moisture naturally, reducing irrigation needs",
            impact: "40% less watering needed",
            icon: "💧",
            timeToSee: "1-2 weeks"
        },
        {
            title: "Reduces Waste",
            description: "Diverts 30% of household waste from landfills",
            impact: "30% waste reduction",
            icon: "♻️",
            timeToSee: "Immediate"
        },
        {
            title: "Carbon Sequestration",
            description: "Captures and stores carbon in soil, fighting climate change",
            impact: "25% carbon reduction",
            icon: "🌍",
            timeToSee: "6-12 months"
        },
        {
            title: "Cost Savings",
            description: "Reduces need for chemical fertilizers and soil amendments",
            impact: "৳5000-15000 saved annually",
            icon: "💰",
            timeToSee: "3-6 months"
        },
        {
            title: "Biodiversity Support",
            description: "Creates habitat for beneficial microorganisms and insects",
            impact: "3x more beneficial organisms",
            icon: "🦋",
            timeToSee: "4-8 weeks"
        }
    ];

    const bangladeshOrganicStats = {
        organicWaste: "70%",
        potential: "25M tons",
        compostValue: "৳15B",
        farmland: "8.5M hectares",
        reduction: "60%",
        jobs: "500K+"
    };

    const compostingSteps = [
        {
            label: "Site Selection",
            description: "Choose a well-drained area with partial shade, easily accessible year-round"
        },
        {
            label: "Base Layer",
            description: "Create a foundation with coarse materials like small branches for drainage"
        },
        {
            label: "Add Materials",
            description: "Layer green and brown materials in 1:3 ratio for optimal decomposition"
        },
        {
            label: "Maintain Conditions",
            description: "Keep moist like a wrung-out sponge and turn regularly for aeration"
        },
        {
            label: "Monitor Progress",
            description: "Check temperature and adjust moisture/aeration as needed"
        },
        {
            label: "Harvest Compost",
            description: "Collect finished dark, crumbly compost after 2-6 months"
        }
    ];

    const localCompostingCenters = [
        {
            city: "Dhaka",
            centers: ["DNCC Composting Plant", "Matuail Organic Center", "Wari Community Compost"],
            contact: "+880-2-8391215",
            services: ["Pickup", "Processing", "Training"],
            schedule: "Daily 6 AM - 6 PM"
        },
        {
            city: "Chittagong",
            centers: ["CCC Organic Facility", "Agrabad Compost Center"],
            contact: "+880-31-2853333",
            services: ["Processing", "Equipment", "Training"],
            schedule: "Daily 7 AM - 5 PM"
        },
        {
            city: "Sylhet",
            centers: ["Sylhet Organic Co-op", "Tea Garden Compost Hub"],
            contact: "+880-821-714491",
            services: ["Pickup", "Training", "Sales"],
            schedule: "Mon-Sat 8 AM - 4 PM"
        }
    ];

    const dosDonts = {
        dos: [
            "Chop large items into smaller pieces",
            "Mix green and brown materials properly",
            "Keep compost moist but not soggy",
            "Turn compost regularly for air circulation",
            "Cover food scraps with dry materials",
            "Monitor temperature (130-160°F ideal)",
            "Add variety of organic materials",
            "Use finished compost within one year"
        ],
        donts: [
            "Add meat, fish, or dairy products",
            "Include diseased plants or weeds with seeds",
            "Add pet waste or cat litter",
            "Use chemically treated materials",
            "Add oils, fats, or grease",
            "Let compost become too dry or too wet",
            "Add plastic or synthetic materials",
            "Ignore bad odors (indicates problems)"
        ]
    };

    const calculateCompostValue = () => {
        const compostPerKg = 2; // BDT per kg of compost produced
        const reductionRatio = 0.3; // 30% volume reduction
        const annualCompost = monthlyOrganic * 12 * reductionRatio;
        const annualValue = annualCompost * compostPerKg;
        const fertilizerSavings = annualCompost * 8; // Replacing chemical fertilizer
        const environmentalValue = annualValue * 3; // Environmental benefit multiplier
        return { annualCompost, annualValue, fertilizerSavings, environmentalValue };
    };

    const { annualCompost, annualValue, fertilizerSavings, environmentalValue } = calculateCompostValue();

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedAccordion(isExpanded ? panel : false);
    };

    return (
        <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c8 100%)' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    background: 'linear-gradient(135deg, #388e3c 0%, #4caf50 100%)',
                    color: 'white',
                    py: { xs: 4, sm: 6, md: 8 },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        backgroundImage: 'radial-gradient(circle at 70% 30%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                        zIndex: 1
                    }
                }}
            >
                <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
                    <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
                        <Grid item xs={12} md={8}>
                            <Button
                                component={Link}
                                to="/"
                                startIcon={<ArrowBack />}
                                sx={{
                                    color: 'white',
                                    mb: { xs: 2, sm: 3 },
                                    fontSize: { xs: '0.8rem', sm: '0.875rem' },
                                    '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' }
                                }}
                            >
                                Back to Home
                            </Button>

                            <Alert
                                severity="success"
                                sx={{
                                    mb: { xs: 2, sm: 3 },
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    '& .MuiAlert-icon': { color: '#4caf50' },
                                    '& .MuiAlert-message': {
                                        fontSize: { xs: '0.8rem', sm: '0.875rem' }
                                    }
                                }}
                            >
                                <Typography fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                    Organic waste comprises 70% of Bangladesh's total waste stream!
                                </Typography>
                            </Alert>

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: { xs: 2, sm: 3 },
                                flexDirection: { xs: 'column', sm: 'row' },
                                textAlign: { xs: 'center', sm: 'left' }
                            }}>
                                <Avatar
                                    sx={{
                                        bgcolor: 'rgba(255,255,255,0.2)',
                                        width: { xs: 60, sm: 70, md: 80 },
                                        height: { xs: 60, sm: 70, md: 80 },
                                        mr: { xs: 0, sm: 2, md: 3 },
                                        mb: { xs: 2, sm: 0 }
                                    }}
                                >
                                    <Nature sx={{ fontSize: { xs: 35, sm: 45, md: 50 }, color: 'white' }} />
                                </Avatar>
                                <Box>
                                    <Typography
                                        variant="h2"
                                        fontWeight={800}
                                        gutterBottom
                                        sx={{
                                            fontSize: { xs: '1.8rem', sm: '2.5rem', md: '3rem' }
                                        }}
                                    >
                                        Organic Waste
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            opacity: 0.9,
                                            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' }
                                        }}
                                    >
                                        Turn waste into nutrient-rich compost for a greener future
                                    </Typography>
                                </Box>
                            </Box>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontSize: { xs: '0.9rem', sm: '1rem', md: '1.2rem' },
                                    lineHeight: 1.6,
                                    opacity: 0.95
                                }}
                            >
                                Organic waste consists of biodegradable materials from plants and animals. When properly composted,
                                this waste becomes valuable fertilizer that enriches soil and reduces the need for chemical fertilizers.
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: { xs: 150, sm: 200, md: 300 },
                                    position: 'relative'
                                }}
                            >
                                <Grass sx={{
                                    fontSize: { xs: 100, sm: 150, md: 200 },
                                    color: 'rgba(255,255,255,0.3)'
                                }} />
                            </Box>
                        </Grid>
                    </Grid>
                </Container>
            </Box>

            <Container maxWidth="lg" sx={{ py: { xs: 3, sm: 4, md: 6 } }}>
                {/* Bangladesh Organic Waste Statistics */}
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        mb: { xs: 4, sm: 6, md: 8 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #f1f8e9 0%, #dcedc8 100%)',
                        border: '2px solid #689f38'
                    }}
                >
                    <Typography
                        variant="h4"
                        fontWeight={700}
                        textAlign="center"
                        gutterBottom
                        sx={{
                            mb: { xs: 2, sm: 3, md: 4 },
                            color: '#33691e',
                            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
                        }}
                    >
                        🇧🇩 Bangladesh Organic Waste Potential
                    </Typography>
                    <Grid container spacing={{ xs: 2, sm: 3 }}>
                        {Object.entries(bangladeshOrganicStats).map(([key, value], index) => (
                            <Grid item xs={6} sm={4} md={2} key={index}>
                                <Box sx={{ textAlign: 'center' }}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#689f38"
                                        gutterBottom
                                        sx={{
                                            fontSize: { xs: '1rem', sm: '1.5rem', md: '2rem' }
                                        }}
                                    >
                                        {value}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            textTransform: 'capitalize',
                                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' }
                                        }}
                                    >
                                        {key.replace(/([A-Z])/g, ' $1').trim()}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Paper>

                {/* Composting Value Calculator */}
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        mb: { xs: 4, sm: 6, md: 8 },
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #fff3e0 0%, #ffcc02 20%, #fff3e0 100%)',
                        border: '2px solid #ff9800'
                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mb: { xs: 2, sm: 3 },
                        flexDirection: { xs: 'column', sm: 'row' },
                        textAlign: { xs: 'center', sm: 'left' }
                    }}>
                        <Calculate sx={{
                            color: '#ff9800',
                            fontSize: { xs: 30, sm: 35, md: 40 },
                            mr: { xs: 0, sm: 2 },
                            mb: { xs: 1, sm: 0 }
                        }} />
                        <Typography
                            variant="h4"
                            fontWeight={700}
                            color="#e65100"
                            sx={{
                                fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
                            }}
                        >
                            🌱 Personal Composting Impact Calculator
                        </Typography>
                    </Box>
                    <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <TextField
                                label="Monthly Organic Waste (kg)"
                                type="number"
                                value={monthlyOrganic}
                                onChange={(e) => setMonthlyOrganic(Number(e.target.value))}
                                fullWidth
                                sx={{ mb: 2 }}
                                size={isMobile ? "small" : "medium"}
                            />
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Grid container spacing={{ xs: 1, sm: 2, md: 3 }} sx={{ textAlign: 'center' }}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#ff9800"
                                        sx={{
                                            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
                                        }}
                                    >
                                        {annualCompost}kg
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' }
                                        }}
                                    >
                                        Compost Produced
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#4caf50"
                                        sx={{
                                            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
                                        }}
                                    >
                                        ৳{annualValue}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' }
                                        }}
                                    >
                                        Compost Value
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#2196f3"
                                        sx={{
                                            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
                                        }}
                                    >
                                        ৳{fertilizerSavings}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' }
                                        }}
                                    >
                                        Fertilizer Savings
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography
                                        variant="h4"
                                        fontWeight={800}
                                        color="#9c27b0"
                                        sx={{
                                            fontSize: { xs: '1.2rem', sm: '1.5rem', md: '2rem' }
                                        }}
                                    >
                                        ৳{environmentalValue}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' }
                                        }}
                                    >
                                        Environmental Value
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Enhanced Organic Categories with Tabs */}
                <Typography
                    variant="h3"
                    fontWeight={700}
                    textAlign="center"
                    gutterBottom
                    sx={{
                        mb: { xs: 3, sm: 4, md: 6 },
                        color: '#2e7d32',
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                    }}
                >
                    Types of Organic Materials
                </Typography>

                <Paper sx={{ mb: { xs: 4, sm: 6, md: 8 }, borderRadius: 4 }}>
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        centered={!isMobile}
                        variant={isMobile ? "scrollable" : "standard"}
                        scrollButtons={isMobile ? "auto" : false}
                        sx={{
                            '& .MuiTab-root': {
                                fontSize: { xs: '0.7rem', sm: '0.8rem', md: '0.875rem' },
                                minWidth: { xs: 'auto', sm: 120 }
                            }
                        }}
                    >
                        <Tab label="Material Types" />
                        <Tab label="Composting Methods" />
                        <Tab label="Benefits & Impact" />
                    </Tabs>

                    <TabPanel value={tabValue} index={0}>
                        <Grid container spacing={4} sx={{ p: 3 }}>
                            {organicItems.map((category, index) => (
                                <Grid item xs={12} md={6} key={index}>
                                    <Card
                                        elevation={4}
                                        sx={{
                                            height: '100%',
                                            borderRadius: 4,
                                            transition: 'all 0.3s ease',
                                            border: '2px solid transparent',
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                boxShadow: '0 12px 40px rgba(76, 175, 80, 0.2)',
                                                borderColor: category.color
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 4 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    <Avatar
                                                        sx={{
                                                            bgcolor: `${category.color}20`,
                                                            width: 60,
                                                            height: 60,
                                                            mr: 2
                                                        }}
                                                    >
                                                        {category.icon}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography variant="h5" fontWeight={600} color={category.color}>
                                                            {category.category}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            {category.decompositionTime}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>

                                            <Box sx={{ mb: 2 }}>
                                                <Grid container spacing={1} sx={{ mb: 2 }}>
                                                    <Grid item xs={12}>
                                                        <Chip
                                                            label={category.carbonRatio}
                                                            size="small"
                                                            color={category.carbonRatio.includes('Green') ? 'success' : 'warning'}
                                                            sx={{ mr: 1 }}
                                                        />
                                                        <Chip
                                                            label={category.compostValue}
                                                            size="small"
                                                            color="info"
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>

                                            <Grid container spacing={1}>
                                                {category.items.map((item, itemIndex) => (
                                                    <Grid item xs={12} sm={6} key={itemIndex}>
                                                        <Chip
                                                            label={item}
                                                            variant="outlined"
                                                            size="small"
                                                            sx={{
                                                                borderColor: category.color,
                                                                color: category.color,
                                                                width: '100%',
                                                                justifyContent: 'flex-start',
                                                                mb: 1,
                                                                '&:hover': {
                                                                    backgroundColor: `${category.color}10`
                                                                }
                                                            }}
                                                        />
                                                    </Grid>
                                                ))}
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>

                    <TabPanel value={tabValue} index={1}>
                        <Grid container spacing={4} sx={{ p: 3 }}>
                            {compostingMethods.map((method, index) => (
                                <Grid item xs={12} md={6} lg={4} key={index}>
                                    <Card
                                        elevation={3}
                                        sx={{
                                            height: '100%',
                                            borderRadius: 4,
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 8px 25px rgba(76, 175, 80, 0.15)'
                                            }
                                        }}
                                    >
                                        <CardContent sx={{ p: 3 }}>
                                            <Box sx={{ textAlign: 'center', mb: 2 }}>
                                                <Typography variant="h2" sx={{ mb: 1 }}>
                                                    {method.icon}
                                                </Typography>
                                                <Typography variant="h6" fontWeight={600} gutterBottom>
                                                    {method.method}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    {method.description}
                                                </Typography>
                                            </Box>

                                            <Box sx={{ mb: 2 }}>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={6}>
                                                        <Chip
                                                            label={method.timeframe}
                                                            size="small"
                                                            color="primary"
                                                            icon={<Timer />}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Chip
                                                            label={method.difficulty}
                                                            size="small"
                                                            color={method.difficulty === 'Easy' ? 'success' : method.difficulty === 'Medium' ? 'warning' : 'error'}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Chip
                                                            label={method.spaceRequired}
                                                            size="small"
                                                            color="info"
                                                        />
                                                    </Grid>
                                                    <Grid item xs={6}>
                                                        <Chip
                                                            label={method.cost}
                                                            size="small"
                                                            color="secondary"
                                                            icon={<AttachMoney />}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </Box>

                                            <Typography variant="body2" color="text.secondary">
                                                <strong>Maintenance:</strong> {method.maintenance}
                                            </Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>

                    <TabPanel value={tabValue} index={2}>
                        <Grid container spacing={4} sx={{ p: 3 }}>
                            {compostingBenefits.map((benefit, index) => (
                                <Grid item xs={12} sm={6} md={4} key={index}>
                                    <Card
                                        elevation={3}
                                        sx={{
                                            p: 3,
                                            textAlign: 'center',
                                            borderRadius: 4,
                                            height: '100%',
                                            background: 'linear-gradient(135deg, #ffffff 0%, #f1f8e9 100%)',
                                            transition: 'all 0.3s ease',
                                            '&:hover': {
                                                transform: 'translateY(-4px)',
                                                boxShadow: '0 8px 25px rgba(76, 175, 80, 0.15)'
                                            }
                                        }}
                                    >
                                        <Typography variant="h2" sx={{ mb: 2 }}>
                                            {benefit.icon}
                                        </Typography>
                                        <Typography variant="h6" fontWeight={600} gutterBottom color="#2e7d32">
                                            {benefit.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                            {benefit.description}
                                        </Typography>
                                        <Chip
                                            label={benefit.impact}
                                            color="success"
                                            size="small"
                                            icon={<TrendingUp />}
                                            sx={{ mb: 1 }}
                                        />
                                        <Typography variant="caption" display="block" color="text.secondary">
                                            Results visible in: {benefit.timeToSee}
                                        </Typography>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </TabPanel>
                </Paper>

                {/* Step-by-Step Composting Guide */}
                <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom sx={{ mb: 6, color: '#2e7d32' }}>
                    Complete Composting Guide
                </Typography>

                <Paper sx={{ p: 4, mb: 8, borderRadius: 4 }}>
                    <Stepper activeStep={activeStep} orientation="vertical">
                        {compostingSteps.map((step, index) => (
                            <Step key={step.label}>
                                <StepLabel
                                    optional={
                                        index === compostingSteps.length - 1 ? (
                                            <Typography variant="caption">Final step</Typography>
                                        ) : null
                                    }
                                >
                                    <Typography variant="h6" fontWeight={600}>
                                        {step.label}
                                    </Typography>
                                </StepLabel>
                                <StepContent>
                                    <Typography sx={{ mb: 2 }}>
                                        {step.description}
                                    </Typography>
                                    <Box sx={{ mb: 2 }}>
                                        <Button
                                            variant="contained"
                                            onClick={() => setActiveStep(index + 1)}
                                            sx={{ mt: 1, mr: 1 }}
                                            disabled={index === compostingSteps.length - 1}
                                        >
                                            {index === compostingSteps.length - 1 ? 'Finish' : 'Continue'}
                                        </Button>
                                        <Button
                                            disabled={index === 0}
                                            onClick={() => setActiveStep(index - 1)}
                                            sx={{ mt: 1, mr: 1 }}
                                        >
                                            Back
                                        </Button>
                                    </Box>
                                </StepContent>
                            </Step>
                        ))}
                    </Stepper>
                    {activeStep === compostingSteps.length && (
                        <Paper square elevation={0} sx={{ p: 3 }}>
                            <Typography>Congratulations! You're ready to start composting successfully!</Typography>
                            <Button onClick={() => setActiveStep(0)} sx={{ mt: 1, mr: 1 }}>
                                Reset Guide
                            </Button>
                        </Paper>
                    )}
                </Paper>

                {/* Local Composting Centers */}
                <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom sx={{ mb: 6, color: '#2e7d32' }}>
                    🏭 Local Composting Centers
                </Typography>

                <Grid container spacing={4} sx={{ mb: 8 }}>
                    {localCompostingCenters.map((center, index) => (
                        <Grid item xs={12} md={4} key={index}>
                            <Card
                                elevation={3}
                                sx={{
                                    p: 3,
                                    borderRadius: 4,
                                    height: '100%',
                                    border: '2px solid #4caf50'
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                    <LocationOn sx={{ color: '#4caf50', mr: 1 }} />
                                    <Typography variant="h5" fontWeight={600} color="#2e7d32">
                                        {center.city}
                                    </Typography>
                                </Box>

                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                    Composting Centers:
                                </Typography>
                                <List dense>
                                    {center.centers.map((loc, idx) => (
                                        <ListItem key={idx} sx={{ px: 0, py: 0.5 }}>
                                            <ListItemIcon sx={{ minWidth: 20 }}>
                                                <Nature sx={{ fontSize: 16, color: '#4caf50' }} />
                                            </ListItemIcon>
                                            <ListItemText primary={loc} />
                                        </ListItem>
                                    ))}
                                </List>

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ mb: 2 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                                        Services:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {center.services.map((service, idx) => (
                                            <Chip
                                                key={idx}
                                                label={service}
                                                size="small"
                                                color="success"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <Phone sx={{ color: '#757575', mr: 1, fontSize: 20 }} />
                                    <Typography variant="body2">{center.contact}</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <Schedule sx={{ color: '#757575', mr: 1, fontSize: 20 }} />
                                    <Typography variant="body2">{center.schedule}</Typography>
                                </Box>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Enhanced Do's and Don'ts */}
                <Typography variant="h3" fontWeight={700} textAlign="center" gutterBottom sx={{ mb: 6, color: '#2e7d32' }}>
                    Composting Guidelines
                </Typography>

                <Grid container spacing={4} sx={{ mb: 8 }}>
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 4,
                                borderRadius: 4,
                                background: 'linear-gradient(135deg, #e8f5e8 0%, #c8e6c8 100%)',
                                border: '2px solid #4caf50'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <CheckCircle sx={{ color: '#4caf50', fontSize: 40, mr: 2 }} />
                                <Typography variant="h4" fontWeight={700} color="#2e7d32">
                                    Essential DO's
                                </Typography>
                            </Box>
                            <List>
                                {dosDonts.dos.map((item, index) => (
                                    <ListItem key={index} sx={{ px: 0 }}>
                                        <ListItemIcon>
                                            <CheckCircle sx={{ color: '#4caf50' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item}
                                            sx={{ '& .MuiListItemText-primary': { fontWeight: 500 } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Paper
                            elevation={3}
                            sx={{
                                p: 4,
                                borderRadius: 4,
                                background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                                border: '2px solid #f44336'
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Cancel sx={{ color: '#f44336', fontSize: 40, mr: 2 }} />
                                <Typography variant="h4" fontWeight={700} color="#c62828">
                                    Critical DON'Ts
                                </Typography>
                            </Box>
                            <List>
                                {dosDonts.donts.map((item, index) => (
                                    <ListItem key={index} sx={{ px: 0 }}>
                                        <ListItemIcon>
                                            <Cancel sx={{ color: '#f44336' }} />
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={item}
                                            sx={{ '& .MuiListItemText-primary': { fontWeight: 500 } }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Paper>
                    </Grid>
                </Grid>

                {/* Enhanced Call to Action */}
                <Paper
                    elevation={6}
                    sx={{
                        p: 6,
                        borderRadius: 4,
                        background: 'linear-gradient(135deg, #ffffff 0%, #f1f8e9 100%)',
                        textAlign: 'center'
                    }}
                >
                    <Typography variant="h3" fontWeight={700} gutterBottom color="#2e7d32">
                        Your Composting Journey Starts Today
                    </Typography>
                    <Divider sx={{ my: 3, maxWidth: 200, mx: 'auto', backgroundColor: '#4caf50' }} />

                    <Grid container spacing={4} sx={{ mb: 4 }}>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                                <EmojiEvents sx={{ fontSize: 48, color: '#ff9800', mb: 2 }} />
                                <Typography variant="h5" fontWeight={600} gutterBottom>
                                    Create Value
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Produce {annualCompost}kg of valuable compost worth ৳{annualValue} annually
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                                <Nature sx={{ fontSize: 48, color: '#4caf50', mb: 2 }} />
                                <Typography variant="h5" fontWeight={600} gutterBottom>
                                    Environmental Impact
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Generate ৳{environmentalValue} worth of environmental benefits
                                </Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Paper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
                                <AttachMoney sx={{ fontSize: 48, color: '#2196f3', mb: 2 }} />
                                <Typography variant="h5" fontWeight={600} gutterBottom>
                                    Cost Savings
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Save ৳{fertilizerSavings} on fertilizer purchases annually
                                </Typography>
                            </Paper>
                        </Grid>
                    </Grid>

                    <Box sx={{ mt: 6 }}>
                        <Button
                            component={Link}
                            to="/classify"
                            variant="contained"
                            size="large"
                            sx={{
                                mr: 2,
                                px: 4,
                                py: 2,
                                background: 'linear-gradient(135deg, #4caf50 0%, #8bc34a 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #388e3c 0%, #689f38 100%)'
                                }
                            }}
                        >
                            Start Composting Today
                        </Button>
                        <Button
                            component={Link}
                            to="/tips"
                            variant="outlined"
                            size="large"
                            sx={{ px: 4, py: 2 }}
                        >
                            Learn More Tips
                        </Button>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default OrganicWastePage;
